import { FormAutoSubmit, TextFieldElement, emailPattern } from '@graphcommerce/ecommerce-ui'
import { useApolloClient, useQuery } from '@graphcommerce/graphql'
import {
  CustomerDocument,
  useAccountSignInUpForm,
  useCustomerQuery,
  ApolloCustomerErrorAlert,
  SignInForm,
  SignUpForm,
} from '@graphcommerce/magento-customer'
import { signOut } from '@graphcommerce/magento-customer/components/SignOutForm/signOut'
import { StoreConfigDocument } from '@graphcommerce/magento-store'
import {
  ActionCard,
  ActionCardListForm,
  Button,
  FormActions,
  FormDiv,
  FormRow,
  LayoutTitle,
  extendableComponent,
  useStorefrontConfig,
} from '@graphcommerce/next-ui'
import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import { Box, CircularProgress, Link, Portal, SxProps, Theme, Typography } from '@mui/material'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { AudienceDocument } from '../../../graphql/Audience.gql'

export type AccountSignInUpFormProps = { sx?: SxProps<Theme> }

const parts = ['root', 'titleContainer'] as const
const { classes } = extendableComponent('AccountSignInUpForm', parts)

export function AccountSignInUpForm(props: AccountSignInUpFormProps) {
  const { sx = [] } = props
  const customerQuery = useCustomerQuery(CustomerDocument)

  const { email, firstname = '' } = customerQuery.data?.customer ?? {}

  const { mode, form, submit, hasAccount } = useAccountSignInUpForm()
  const { formState, watch, control, error } = form
  const router = useRouter()
  const { query } = useRouter()

  const client = useApolloClient()

  const { data: config } = useQuery(StoreConfigDocument)
  const storeCode = config?.storeConfig?.store_code ?? ''
  const isB2BStore = useStorefrontConfig().magentoStoreCode.includes('b2b')

  const { data: audience } = useCustomerQuery(AudienceDocument)
  const customerType = audience?.customer?.audience
  const [loginType, setLoginType] = useState<false | 'b2c' | 'b2b'>(false)
  const [showPortal, setShowPortal] = useState<boolean>(false)

  const b2bRegistrationLinks = {
    b2b_en: 'become-a-customer',
    b2b_nl: 'klant-worden',
    b2b_fr: 'devenir-client',
    b2b_de: 'kunde-werden',
    b2b_it: 'diventare-un-cliente',
    b2b_es: 'convertirse-en-un-cliente',
  }

  const b2bRegistrationLink =
    b2bRegistrationLinks && storeCode ? b2bRegistrationLinks[storeCode] : null

  const redirectAfterLoginUrl = useRef('/')

  function removeContent() {
    const nextWrapper: HTMLElement | null = document.querySelector('#__next')

    if (nextWrapper) {
      nextWrapper.style.opacity = '0'
    }
  }

  const redirectToB2C = useCallback(() => {
    setShowPortal(true)

    setTimeout(async () => {
      const newUrl = window.location.href.includes('b2b-rc')
        ? `https://b2c-rc.vaessen-creative.com${window.location.pathname}?loginType=b2c`
        : `https://www.vaessen-creative.com${window.location.pathname}?loginType=b2c`

      await router.replace(newUrl)
    })
  }, [router])

  const redirectToB2B = useCallback(() => {
    setShowPortal(true)

    setTimeout(async () => {
      const newUrl = window.location.href.includes('b2c-rc')
        ? `https://b2b-rc.vaessen-creative.com${window.location.pathname}?loginType=b2b`
        : `https://b2b.vaessen-creative.com${window.location.pathname}?loginType=b2b`

      await router.replace(newUrl)
    })
  }, [router])

  useEffect(() => {
    if (mode === 'signedin') {
      const loginEvent = isB2BStore ? 'login_b2b' : 'login_b2c'

      globalThis.gtag?.('event', loginEvent, {
        method: 'Website',
      })
    }

    // after the user is logged in, we know the customerType
    if (customerType === 'b2c' && isB2BStore) {
      console.log('Redirecting b2c customer to b2c site')
      removeContent()
      client.resetStore().catch((e) => e)

      // eslint-disable-next-line no-alert
      alert(
        i18n._(
          /* i18n */ 'Dear customer, you are trying to log in to our business website with a consumer account. You will be redirected to the correct website where you can log in.',
        ),
      )

      redirectToB2C()
    }

    if (customerType === 'b2b' && !isB2BStore) {
      console.log('Redirecting b2b customer to b2b site')
      removeContent()
      client.resetStore().catch((e) => e)

      // eslint-disable-next-line no-alert
      alert(
        i18n._(
          /* i18n */ 'Dear customer, you are trying to log in to our consumer website with a business account. You will be redirected to the correct website where you can log in.',
        ),
      )

      redirectToB2B()
    }

    if (window.location.href.includes('loginType=b2b')) {
      setLoginType('b2b')
    }

    if (window.location.href.includes('loginType=b2c')) {
      setLoginType('b2c')
    }

    if (window.localStorage.getItem('AFTER_LOGIN_REDIRECT_URL')) {
      redirectAfterLoginUrl.current = window.localStorage.getItem('AFTER_LOGIN_REDIRECT_URL') ?? '/'
    }
  }, [isB2BStore, customerType, client, mode, redirectToB2C, redirectToB2B])

  return (
    <FormDiv sx={sx} className={classes.root}>
      <Box
        className={classes.titleContainer}
        sx={(theme) => ({ typography: 'body1', marginBottom: theme.spacings.xs })}
      >
        {mode === 'email' ||
          (mode === 'signup' && hasAccount === false && loginType && isB2BStore && (
            <>
              <LayoutTitle variant='h2' gutterBottom={false}>
                <Trans id='Sign in or create an account!' />
              </LayoutTitle>
              <Typography variant='h6' align='center'>
                <Trans id='Fill in your e-mail to login or create an account' />
              </Typography>
              {b2bRegistrationLink && (
                <Typography
                  component='p'
                  variant='h6'
                  align='center'
                  sx={{ a: { color: 'primary.main' } }}
                >
                  <Trans
                    id='Commercial customer? Use our <0>registration form</0>'
                    components={{
                      0: <Link href={b2bRegistrationLink} />,
                    }}
                  />
                </Typography>
              )}
            </>
          ))}

        {mode !== 'signedin' && mode !== 'session-expired' && !loginType && (
          <>
            <LayoutTitle variant='h2' gutterBottom={false}>
              <Trans id='Sign in or create an account!' />
            </LayoutTitle>
            <Typography variant='h6' align='center'>
              <Trans id='Are you a consumer or business customer?' />
            </Typography>
            {b2bRegistrationLink && (
              <Typography
                component='p'
                variant='h6'
                align='center'
                sx={{ a: { color: 'primary.main' } }}
              >
                <Trans
                  id='Commercial customer? Use our <0>registration form</0>'
                  components={{
                    0: <Link href={b2bRegistrationLink} />,
                  }}
                />
              </Typography>
            )}
          </>
        )}

        {mode === 'signin' && loginType && (
          <>
            <LayoutTitle variant='h2' gutterBottom={false}>
              <Trans id='Sign in' />
            </LayoutTitle>
            <Typography variant='h6' align='center'>
              <Trans id='Fill in your password' />
            </Typography>
          </>
        )}

        {mode === 'signup' && loginType && !isB2BStore && (
          <>
            <LayoutTitle variant='h2' gutterBottom={false}>
              <Trans id='Create account!' />
            </LayoutTitle>
            <Typography variant='h6' align='center'>
              <Trans id='Create a password and tell us your name' />
            </Typography>
          </>
        )}

        {mode === 'signedin' && (
          <>
            <LayoutTitle variant='h2' gutterBottom={false}>
              <Trans id='Hi {firstname}! You’re now logged in!' values={{ firstname }} />
            </LayoutTitle>
            <Typography variant='h6' align='center'>
              <Link href='/account' underline='hover' color='secondary'>
                <Trans id='View your account' />
              </Link>
            </Typography>

            <FormActions>
              <Button
                href={redirectAfterLoginUrl.current}
                variant='pill'
                color='secondary'
                size='large'
                onClick={() => {
                  window.localStorage.removeItem('AFTER_LOGIN_REDIRECT_URL')
                }}
              >
                <Trans id='Continue shopping' />
              </Button>
            </FormActions>
          </>
        )}

        {mode === 'session-expired' && (
          <>
            <LayoutTitle variant='h2' gutterBottom={false}>
              <Trans id='Your session is expired' />
            </LayoutTitle>
            <Typography variant='h6' align='center'>
              <Trans id='Log in to continue shopping' />
            </Typography>
          </>
        )}
      </Box>

      {!import.meta.graphCommerce.enableGuestCheckoutLogin &&
        (mode === 'signin' || mode === 'signup' || mode === 'email') &&
        loginType && (
          <FormRow>
            <ActionCardListForm
              control={form.control}
              name='requestedMode'
              defaultValue={mode}
              layout='grid'
              size='large'
              render={ActionCard}
              sx={(theme) => ({
                '&.layoutGrid': {
                  gridTemplateColumns: 'auto auto',
                  justifyContent: 'center',
                },
                '& .ActionCard-root.sizeLarge': {
                  px: theme.spacings.md,
                },
              })}
              items={
                !isB2BStore
                  ? [
                      { value: 'signin', title: <Trans id='Sign in' /> },
                      { value: 'signup', title: <Trans id='Create Account' /> },
                    ]
                  : []
              }
            />
          </FormRow>
        )}

      {mode !== 'signedin' && loginType && (
        <form noValidate onSubmit={submit}>
          <FormAutoSubmit {...form} submit={submit} />
          <Box>
            <FormRow>
              <TextFieldElement
                variant='outlined'
                control={control}
                name='email'
                value={query.email}
                required
                type='email'
                rules={{
                  required: true,
                  pattern: { value: emailPattern, message: '' },
                }}
                error={formState.isSubmitted && !!formState.errors.email}
                label={<Trans id='Email' />}
                InputProps={{
                  endAdornment:
                    mode === 'session-expired' ? (
                      <Button
                        type='submit'
                        variant='inline'
                        color='primary'
                        loading={formState.isSubmitting}
                        sx={{ whiteSpace: 'nowrap' }}
                        onClick={() => {
                          signOut(client)
                          form.resetField('email')
                        }}
                      >
                        <Trans id='Sign out' />
                      </Button>
                    ) : (
                      formState.isSubmitting && <CircularProgress sx={{ display: 'inline-flex' }} />
                    ),
                  readOnly: !!email,
                }}
              />
            </FormRow>
          </Box>

          <ApolloCustomerErrorAlert error={error} />

          {mode === 'email' && (
            <Box>
              <FormActions>
                <Button
                  type='submit'
                  loading={formState.isSubmitting}
                  variant='pill'
                  color='primary'
                  size='large'
                >
                  <Trans id='Continue' />
                </Button>
              </FormActions>
            </Box>
          )}
        </form>
      )}

      {mode !== 'signedin' && !loginType && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', placeContent: 'center' }}>
          <Button
            variant='pill'
            color='primary'
            onClick={() => {
              setLoginType('b2c')

              if (isB2BStore) {
                redirectToB2C()
              }
            }}
          >
            <Trans id='Consumer' />
          </Button>
          <Button
            variant='pill'
            color='secondary'
            onClick={() => {
              setLoginType('b2b')

              if (!isB2BStore) {
                redirectToB2B()
              }
            }}
          >
            <Trans id='Business' />
          </Button>
        </Box>
      )}

      {(mode === 'signin' || mode === 'session-expired') && loginType && (
        <Box>
          <SignInForm email={watch('email')} />
        </Box>
      )}

      {mode === 'signup' && hasAccount === false && isB2BStore && (
        <Trans
          id="There is no B2B account registered to this e-mail. Please verify you've entered the correct e-mail adress or contact our customer service. If you do not have a B2B account, but wish to apply for one, you may do so <0>here</0>."
          components={{
            0: <Link href={b2bRegistrationLink} />,
          }}
        />
      )}

      {mode === 'signup' && loginType && !isB2BStore && (
        <Box>
          <SignUpForm email={watch('email')} />
        </Box>
      )}

      {showPortal && (
        <Portal>
          <Box
            sx={{
              position: 'fixed',
              zIndex: 99999,
              backgroundColor: 'background.paper',
              height: '100%',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                textAlign: 'center',
              }}
            >
              <CircularProgress />
              <Typography variant='subtitle1'>
                <Trans id='You will be redirected to the correct website where you can log in.' />
              </Typography>
            </Box>
          </Box>
        </Portal>
      )}
    </FormDiv>
  )
}
